import * as React from "react"
import { cn } from "@/lib/utils"
import { Clock } from "lucide-react"

export interface ActivityItem {
  id: string
  action: string
  target: string
  timestamp: Date | string
  user?: string
  icon?: React.ReactNode
}

export interface ActivityListProps extends React.HTMLAttributes<HTMLDivElement> {
  items: ActivityItem[]
  emptyMessage?: string
}

function formatTimestamp(timestamp: Date | string): string {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "Just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function ActivityList({
  items,
  emptyMessage = "No recent activity",
  className,
  ...props
}: ActivityListProps) {
  if (items.length === 0) {
    return (
      <div className={cn("text-center py-8 text-muted-foreground", className)} {...props}>
        <p>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={cn("space-y-2", className)} {...props}>
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
        >
          {item.icon && (
            <div className="mt-0.5 text-muted-foreground">
              {item.icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">{item.action}</span>
              <span className="text-sm text-muted-foreground">{item.target}</span>
            </div>
            {item.user && (
              <p className="text-xs text-muted-foreground mt-1">by {item.user}</p>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <Clock className="h-3 w-3" />
            <span>{formatTimestamp(item.timestamp)}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

